/**
 * knownX1Tokens — canonical X1 native-token identity overrides.
 *
 * X1 is a Solana FORK. Its wrapped-native SPL mint reuses Solana's well-known
 * address `So111…112`, which the community Solana token list labels "Wrapped
 * SOL" / symbol `SOL`. On X1 that mint is wrapped **XNT**, not SOL. Likewise
 * the native balance ("native") and the XDEX-listed XNT mint should render the
 * real XNT logo, not a generic coin badge.
 *
 * This is the single source of truth for those identities. It is overlaid at
 * the SHARED layers (tokenMetadataService + TokenCell) so every consumer —
 * the tx-detail Token Balances table, the account page, /tokens, and the
 * portfolio — gets the corrected symbol / name / logo without page-level edits.
 */

/** Wrapped-native SPL mint. On X1 this is wrapped XNT (NOT "Wrapped SOL"). */
export const WRAPPED_NATIVE_MINT = 'So11111111111111111111111111111111111111112'

/** Sentinel mint used across the app for the native (un-wrapped) XNT balance. */
export const NATIVE_MINT_SENTINEL = 'native'

/** XDEX-listed XNT SPL mint (shown on /tokens). */
export const XNT_SPL_MINT = '818H94YT4FgLouz8fB8FNbzpozSqHE9Qx1N1WdJXvUKo'

/**
 * X1-side wrapped-SOL mint (bridge_token_registry, chain='x1'). The Warp bridge
 * enabled wSOL on 2026-09-01. Because X1 is a Solana FORK, its ON-CHAIN
 * representation of any bridged asset renders with the X1 (XNT) mark — the
 * Solana-side leg carries the Solana mark separately (bridge signature links).
 */
export const X1_WSOL_MINT = 'JDqX4vau2P5zJmLpuNitvR6vMURr9kYjex6oZQXz3Ja8'

/** Canonical XNT logo — same asset the portfolio's native XNT position uses. */
export const XNT_LOGO = 'https://app.xdex.xyz/assets/images/tokens/x1.webp'

export interface X1TokenIdentity {
  symbol: string
  name: string
  logo: string
}

/**
 * mint → canonical X1 identity. Covers the native sentinel, the wrapped-native
 * SPL mint, and the XDEX XNT mint. For consistency with the portfolio we use
 * the x1.webp logo for all three.
 */
export const X1_TOKEN_OVERRIDES: Record<string, X1TokenIdentity> = {
  [NATIVE_MINT_SENTINEL]: { symbol: 'XNT', name: 'XNT', logo: XNT_LOGO },
  [WRAPPED_NATIVE_MINT]: { symbol: 'wXNT', name: 'Wrapped XNT', logo: XNT_LOGO },
  [XNT_SPL_MINT]: { symbol: 'XNT', name: 'XNT', logo: XNT_LOGO },
  // X1's on-chain wSOL uses the X1 (XNT) mark — X1 is a Solana fork, so its
  // representation of the bridged asset carries the fork's icon. The canonical
  // symbol stays wSOL (unlike wrapped-native, which is wXNT).
  [X1_WSOL_MINT]: { symbol: 'wSOL', name: 'Wrapped SOL', logo: XNT_LOGO },
}

/** Return the canonical X1 identity for a mint, or undefined if not a known X1 mint. */
export function getX1TokenIdentity(mint?: string | null): X1TokenIdentity | undefined {
  if (!mint) return undefined
  return X1_TOKEN_OVERRIDES[mint]
}

/** A resolved-metadata shape as returned by tokenMetadataService (image = logo). */
export interface X1OverlayableMeta {
  name?: string
  symbol?: string
  image?: string
  [k: string]: unknown
}

/**
 * Overlay the canonical X1 identity onto an already-resolved metadata object.
 * The override takes PRECEDENCE for known X1 mints (so a community-list "SOL"
 * result never wins). Non-X1 mints are returned untouched.
 */
export function applyX1TokenOverride<T extends X1OverlayableMeta>(
  mint: string | undefined | null,
  meta: T,
): T {
  const identity = getX1TokenIdentity(mint)
  if (!identity) return meta
  return {
    ...meta,
    symbol: identity.symbol,
    name: identity.name,
    image: identity.logo,
  }
}
